import React, { useState } from 'react';
import { ClipboardIcon, ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon, XCircleIcon, Loader2Icon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
const AISuggestionsWidget = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<{
    [key: number]: number;
  }>({});
  const [showResults, setShowResults] = useState(false);
  const [isAcceptingChallenge, setIsAcceptingChallenge] = useState(false);
  const [challengeAccepted, setChallengeAccepted] = useState(false);
  const lessonSummaryText = `Basato sulla tua ultima sessione su "Cosa cambia a +1,5°C e +2°C", ecco i punti chiave:

• Mezzo grado fa una differenza enorme su eventi estremi e impatti ecosistemici
• A +2°C aumentano drasticamente ondate di calore, siccità e perdita di biodiversità
• Limitare il riscaldamento a +1,5°C riduce significativamente i rischi per le comunità vulnerabili`;
  const questions = [{
    text: '1) Mezzo grado in più incide soprattutto su…',
    answers: ['Eventi estremi', 'Prezzo energia', 'Migrazioni stagionali'],
    correctAnswer: 0
  }, {
    text: '2) A +2°C aumenta in modo più marcato…',
    answers: ['Ondate di calore e siccità', 'Solo le piogge invernali', 'Solo la temperatura del mare'],
    correctAnswer: 0
  }, {
    text: '3) Limitare a +1,5°C riduce soprattutto i rischi per…',
    answers: ['Comunità vulnerabili', 'Traffico urbano', 'Acquisti online'],
    correctAnswer: 0
  }];
  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(lessonSummaryText);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };
  const handleAnswerClick = (answerIndex: number) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestion]: answerIndex
    });
    // Auto-advance to next question after selection (except on last question)
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
      }, 300);
    }
  };
  const handleSubmitQuiz = () => {
    setShowResults(true);
  };
  const handleAcceptChallenge = () => {
    setIsAcceptingChallenge(true);
    setTimeout(() => {
      setIsAcceptingChallenge(false);
      setChallengeAccepted(true);
    }, 1500);
  };
  const calculateScore = () => {
    let correct = 0;
    questions.forEach((question, idx) => {
      if (selectedAnswers[idx] === question.correctAnswer) {
        correct++;
      }
    });
    return correct;
  };
  const canGoPrev = currentQuestion > 0;
  const hasAnsweredCurrent = selectedAnswers[currentQuestion] !== undefined;
  const canGoNext = currentQuestion < questions.length - 1 && hasAnsweredCurrent;
  const allAnswered = Object.keys(selectedAnswers).length === questions.length;
  return <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1131">
      <div className="p-4 border-b border-gray-200" data-id="element-1132">
        <h2 className="text-base font-semibold text-gray-800" data-id="element-1133">
          Greenback Intelligence
        </h2>
        <p className="text-xs text-gray-500 font-normal mt-1" data-id="element-1134">
          Suggerimenti personalizzati
        </p>
      </div>
      <div className="p-4" data-id="element-1135">
        <div className="space-y-4" data-id="element-1136">
          {/* Lesson Summary - Clean white background */}
          <div className="rounded-lg p-3 shadow-sm relative bg-white border border-gray-200" data-id="element-1137">
            <div className="mb-2" data-id="element-1138">
              <div className="font-medium text-gray-800 text-sm" data-id="element-1139">
                Riassunto della Lezione
              </div>
            </div>
            <div className="relative" data-id="element-1140">
              <div className={`${isExpanded ? '' : 'max-h-24'} overflow-hidden`} data-id="element-1141">
                <p className="text-sm text-gray-600 font-normal mb-2 leading-relaxed" data-id="element-1142">
                  Basato sulla tua ultima sessione su "Cosa cambia a +1,5°C e
                  +2°C", ecco i punti chiave:
                </p>
                <div className="space-y-1" data-id="element-1143">
                  <div className="flex items-start gap-1.5" data-id="element-1144">
                    <span className="flex-shrink-0 w-1 h-1 rounded-full mt-1.5 bg-gray-400" data-id="element-1145"></span>
                    <p className="text-xs text-gray-700 leading-relaxed" data-id="element-1146">
                      Mezzo grado fa una differenza enorme su eventi estremi e
                      impatti ecosistemici
                    </p>
                  </div>
                  <div className="flex items-start gap-1.5" data-id="element-1147">
                    <span className="flex-shrink-0 w-1 h-1 rounded-full mt-1.5 bg-gray-400" data-id="element-1148"></span>
                    <p className="text-xs text-gray-700 leading-relaxed" data-id="element-1149">
                      A +2°C aumentano drasticamente ondate di calore, siccità e
                      perdita di biodiversità
                    </p>
                  </div>
                  <div className="flex items-start gap-1.5" data-id="element-1150">
                    <span className="flex-shrink-0 w-1 h-1 rounded-full mt-1.5 bg-gray-400" data-id="element-1151"></span>
                    <p className="text-xs text-gray-700 leading-relaxed" data-id="element-1152">
                      Limitare il riscaldamento a +1,5°C riduce
                      significativamente i rischi per le comunità vulnerabili
                    </p>
                  </div>
                </div>
              </div>
              {!isExpanded && <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none" data-id="element-1153"></div>}
            </div>
            <div className="mt-3 flex justify-between items-center" data-id="element-1154">
              <button onClick={() => setIsExpanded(!isExpanded)} className="text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors" data-id="element-1155">
                {isExpanded ? 'Riduci' : 'Leggi tutto'}
              </button>
              <button onClick={handleCopyToClipboard} className="flex items-center text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors" data-id="element-1156">
                <ClipboardIcon size={12} className="mr-1" strokeWidth={1.5} data-id="element-1157" />
                Copia negli appunti
              </button>
            </div>
          </div>

          {/* Sfida proposta - Single challenge with emoji */}
          <div className="rounded-lg p-3 shadow-sm relative bg-white border border-gray-200" data-id="element-1158">
            <div className="mb-2" data-id="element-1159">
              <div className="font-medium text-gray-800 text-sm" data-id="element-1160">
                Sfida proposta
              </div>
            </div>
            <p className="text-sm text-gray-600 font-normal leading-relaxed mb-3" data-id="element-1161">
              Metti in pratica quanto appreso con questa micro-challenge
              personalizzata.
            </p>
            <div className="p-3 bg-gray-50 rounded border border-gray-200" data-id="element-1162">
              <div className="text-2xl mb-2" data-id="element-1163">🚗</div>
              <div className="text-sm font-medium text-gray-800 mb-1" data-id="element-1164">
                3 giorni senza auto (spostamenti brevi)
              </div>
              <div className="text-xs text-gray-500 mb-3" data-id="element-1165">
                Difficoltà: Media • Durata: 3 giorni • Focus: trasporti
              </div>

              {!challengeAccepted ? <button onClick={handleAcceptChallenge} disabled={isAcceptingChallenge} className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50 flex items-center" data-id="element-1166">
                  {isAcceptingChallenge ? <>
                      <Loader2Icon size={14} className="mr-1.5 animate-spin" data-id="element-1167" />
                      Attivazione...
                    </> : 'Accetta sfida'}
                </button> : <Link to="/challenges" className="text-sm font-medium text-green-600 hover:text-green-700 transition-colors flex items-center" data-id="element-1168">
                  <CheckCircleIcon size={14} className="mr-1.5" data-id="element-1169" />
                  Vai alle tue sfide →
                </Link>}
            </div>
          </div>

          {/* Micro-quiz (2 min) - Interactive with navigation */}
          <div className="rounded-lg p-3 shadow-sm relative bg-white border border-gray-200" data-id="element-1170">
            <div className="mb-2" data-id="element-1171">
              <div className="font-medium text-gray-800 text-sm" data-id="element-1172">
                Micro-quiz (2 min)
              </div>
            </div>
            <p className="text-sm text-gray-600 font-normal leading-relaxed mb-3" data-id="element-1173">
              3 domande rapide per fissare i concetti chiave di{' '}
              <span className="font-medium text-gray-900" data-id="element-1174">+1,5°C vs +2°C</span>.
            </p>

            {!showResults ? <>
                {/* Question indicator dots - no green */}
                <div className="flex items-center justify-center gap-2 mb-4" data-id="element-1175">
                  {questions.map((_, idx) => <div key={idx} className={`h-1.5 rounded-full transition-all ${idx === currentQuestion ? 'w-6 bg-gray-800' : selectedAnswers[idx] !== undefined ? 'w-1.5 bg-gray-400' : 'w-1.5 bg-gray-300'}`} data-id="element-1176" />)}
                </div>

                {/* Animated question container */}
                <div className="relative overflow-hidden min-h-[120px]" data-id="element-1177">
                  <AnimatePresence mode="wait" data-id="element-1178">
                    <motion.div key={currentQuestion} initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -20
                }} transition={{
                  duration: 0.3
                }} className="p-3 bg-gray-50 rounded border border-gray-200" data-id="element-1179">
                      <div className="text-xs font-medium text-gray-800 mb-3" data-id="element-1180">
                        {questions[currentQuestion].text}
                      </div>
                      <div className="flex flex-wrap gap-2" data-id="element-1181">
                        {questions[currentQuestion].answers.map((answer, idx) => <button key={idx} onClick={() => handleAnswerClick(idx)} className={`px-3 py-1.5 text-xs rounded border transition-all ${selectedAnswers[currentQuestion] === idx ? 'border-green-600 bg-green-50 text-green-700 font-medium' : 'border-gray-200 bg-white hover:bg-gray-50'}`} data-id="element-1182">
                              {answer}
                            </button>)}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Navigation controls */}
                <div className="mt-4 flex items-center justify-between" data-id="element-1183">
                  <div className="flex items-center gap-2" data-id="element-1184">
                    <button onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))} disabled={!canGoPrev} className={`p-1.5 rounded transition-colors ${canGoPrev ? 'hover:bg-gray-100 text-gray-700' : 'text-gray-300 cursor-not-allowed'}`} data-id="element-1185">
                      <ChevronLeftIcon size={16} strokeWidth={2} data-id="element-1186" />
                    </button>
                    <button onClick={() => setCurrentQuestion(Math.min(questions.length - 1, currentQuestion + 1))} disabled={!canGoNext} className={`p-1.5 rounded transition-colors ${canGoNext ? 'hover:bg-gray-100 text-gray-700' : 'text-gray-300 cursor-not-allowed'}`} data-id="element-1187">
                      <ChevronRightIcon size={16} strokeWidth={2} data-id="element-1188" />
                    </button>
                  </div>

                  <button onClick={handleSubmitQuiz} disabled={!allAnswered} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${allAnswered ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-1189">
                    Conferma e invia
                  </button>
                </div>
              </> /* Results view */ : <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} className="space-y-4" data-id="element-1190">
                {/* Score summary */}
                <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1191">
                  <div className="text-2xl font-semibold text-gray-900 mb-1" data-id="element-1192">
                    {calculateScore()}/{questions.length}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-1193">
                    {calculateScore() === questions.length ? 'Perfetto! Hai risposto correttamente a tutte le domande' : calculateScore() >= 2 ? 'Ottimo lavoro! Hai capito i concetti principali' : 'Rivedi la lezione per consolidare i concetti'}
                  </div>
                </div>

                {/* Answer breakdown */}
                <div className="space-y-3" data-id="element-1194">
                  {questions.map((question, idx) => {
                const isCorrect = selectedAnswers[idx] === question.correctAnswer;
                return <div key={idx} className="p-3 bg-gray-50 rounded border border-gray-200" data-id="element-1195">
                        <div className="flex items-start gap-2 mb-2" data-id="element-1196">
                          {isCorrect ? <CheckCircleIcon size={16} className="text-green-600 flex-shrink-0 mt-0.5" strokeWidth={2} data-id="element-1197" /> : <XCircleIcon size={16} className="text-red-500 flex-shrink-0 mt-0.5" strokeWidth={2} data-id="element-1198" />}
                          <div className="flex-1" data-id="element-1199">
                            <div className="text-xs font-medium text-gray-800 mb-1" data-id="element-1200">
                              {question.text}
                            </div>
                            <div className="text-xs text-gray-600" data-id="element-1201">
                              La tua risposta:{' '}
                              <span className={isCorrect ? 'text-green-600 font-medium' : 'text-red-500 font-medium'} data-id="element-1202">
                                {question.answers[selectedAnswers[idx]]}
                              </span>
                            </div>
                            {!isCorrect && <div className="text-xs text-gray-600 mt-1" data-id="element-1203">
                                Risposta corretta:{' '}
                                <span className="text-green-600 font-medium" data-id="element-1204">
                                  {question.answers[question.correctAnswer]}
                                </span>
                              </div>}
                          </div>
                        </div>
                      </div>;
              })}
                </div>

                {/* Retry button */}
                <button onClick={() => {
              setShowResults(false);
              setCurrentQuestion(0);
              setSelectedAnswers({});
            }} className="w-full px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1205">
                  Riprova il quiz
                </button>
              </motion.div>}
          </div>
        </div>
      </div>
    </div>;
};
export default AISuggestionsWidget;